/* eslint-disable react/no-array-index-key */
import React, { useMemo, useContext, Fragment } from 'react';
import Close from '@material-ui/icons/Close';
import Check from '@material-ui/icons/Check';
import TrendingUp from '@material-ui/icons/TrendingUp';
import Class from '@material-ui/icons/Class';
import AssignmentTurnedIn from '@material-ui/icons/AssignmentTurnedIn';
import ChatBubble from '@material-ui/icons/ChatBubble';
import Group from '@material-ui/icons/Group';
import LocationOff from '@material-ui/icons/LocationOff';
import FlightLand from '@material-ui/icons/FlightLand';
import FlightTakeoff from '@material-ui/icons/FlightTakeoff';
import { Tooltip } from '@material-ui/core';
import { VisitsGlobalContext } from '@mummssoftware/global-context';
import {
  getRefusedHopeStatus,
  getAcceptedHopeStatus,
  getHopeAttributes,
  getElectionAddendumVisitStatus,
} from '@mummssoftware/utils/hope';
import { getVisitStatusLabel, sortAlphaByName } from '../Context/visitLogUtils';
import { VisitLogContext } from '../Context/VisitLogContext';
import {
  M,
  F,
  N,
  H,
  T,
  F2F,
  distanceThresholdMiles,
} from '../Context/constants';
import {
  FilterDotDiv,
  StatusDotDiv,
  CountCell,
  HCPCSCell,
} from '../../components/layout/WrapperDiv';
import { countValueGetter } from './valueGetters';
import {
  select,
  notes,
  hcpcs,
  measures,
  faceToFace,
  tasks,
  questionnaires,
  arriveDistance,
  departDistance,
  noLocation,
  cpcVisit,
} from '../../translations/en.json';

const dotMapper = (attributes: any) =>
  attributes &&
  attributes.map((att: any) => {
    if (!att) return '';
    const mod = att?.shortname?.length <= 3 ? 12 : 10;
    return (
      att.isselected && (
        <span key={att?.shortname || 'Select'}>
          <FilterDotDiv
            style={{
              border: `solid ${att.hex} .2em`,
              width: `${att.shortname.length * mod}px`,
            }}
          >
            <>{`${att.shortname}`}</>
          </FilterDotDiv>
        </span>
      )
    );
  });

export const DotRenderer = (props: any) => {
  const { visitDetails } = props.data;
  const { filteringAttributes, filterAttributes } = useContext(
    VisitsGlobalContext,
  );
  const visitAtts =
    visitDetails &&
    JSON.parse(visitDetails?.visitattrsumdto).sort(sortAlphaByName);
  const selectedAtts =
    (visitAtts && visitAtts.filter((att: any) => att?.isselected)) || [];
  let dots: any[] = [];
  selectedAtts.forEach((att: any) => {
    if (
      filterAttributes &&
      filteringAttributes &&
      filteringAttributes.includes(att.id)
    ) {
      dots = dots.concat(filterAttributes.find((a: any) => a.id === att.id));
    }
  });
  return <div style={{ display: 'flex' }}>{dotMapper(dots)}</div>;
};

export const DistanceRenderer = (params: any) => {
  const { value, data: row } = params;
  const isCpcVisit = !!row?.cpcvisitid;
  const locationText = isCpcVisit ? cpcVisit : noLocation;

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
      }}
    >
      {value ? (
        <Tooltip title={`${value} mi`}>
          {Number(value) < distanceThresholdMiles ? (
            <Check style={{ fontSize: 15 }} />
          ) : (
            <Close style={{ fontSize: 15, color: 'red' }} />
          )}
        </Tooltip>
      ) : (
        <Tooltip title={locationText}>
          <LocationOff style={{ fontSize: 15, color: 'black' }} />
        </Tooltip>
      )}
    </div>
  );
};

export const FilterCellRenderer = (
  filteringAtts: any,
  filterAttributes: any,
) => (
  <>
    {filteringAtts.map((att: any, index: number) => {
      const displayAttribute = filterAttributes.find(
        (a: any) => a.id === att,
      ) || { shortname: `${select}...` };
      return (
        <Fragment key={displayAttribute?.shortname}>
          <span
            key={`span-${displayAttribute?.shortname}`}
            style={
              (displayAttribute?.shortname !== `${select}...` && {
                textDecoration: 'underLine overLine',
                textDecorationThickness: '3px',
                textDecorationColor: displayAttribute?.hex,
              }) ||
              {}
            }
          >
            {displayAttribute?.shortname}
          </span>
          {index < filteringAtts.length - 1 && (
            <span key={`span-comma-${index}`}>, </span>
          )}
        </Fragment>
      );
    })}
  </>
);
export const HCPCSCellRenderer = (props: any) => {
  const { data } = props;
  const { detailsCache } = useContext(VisitLogContext);
  const details = useMemo(() => detailsCache[data.id] || data, [
    detailsCache,
    data,
  ]);

  const hcpcsdata =
    details.physicianservices && JSON.parse(details.physicianservices);

  const count = hcpcsdata && hcpcsdata.length;
  return (
    <Tooltip title={hcpcs}>
      <HCPCSCell>{count}</HCPCSCell>
    </Tooltip>
  );
};
export const CountCellRenderer = (props: any) => {
  const count = countValueGetter(props);
  if (count)
    switch (props.colDef.field) {
      case M:
        return (
          <Tooltip title={measures}>
            <CountCell>{count}</CountCell>
          </Tooltip>
        );
      case F:
        return (
          <Tooltip title={questionnaires}>
            <CountCell>{count}</CountCell>
          </Tooltip>
        );
      case T:
        return (
          <Tooltip title={tasks}>
            <CountCell>{count}</CountCell>
          </Tooltip>
        );
      case N:
        return (
          <Tooltip title={notes}>
            <CountCell>{count}</CountCell>
          </Tooltip>
        );
      case F2F:
        return (
          <Tooltip title={faceToFace}>
            <CountCell>{count}</CountCell>
          </Tooltip>
        );
      case arriveDistance:
        return (
          <Tooltip title={arriveDistance}>
            <CountCell>{count}</CountCell>
          </Tooltip>
        );
      case departDistance:
        return (
          <Tooltip title={departDistance}>
            <CountCell>{count}</CountCell>
          </Tooltip>
        );

      default:
        break;
    }

  return <div />;
};

export const HeaderCellRenderer = (props: any) => {
  const headerIconStyle = { fontSize: 13, marginLeft: '2px' };
  switch (props?.column?.colId) {
    case M:
      return (
        <Tooltip title={measures}>
          <TrendingUp style={headerIconStyle} />
        </Tooltip>
      );
    case F:
      return (
        <Tooltip title={questionnaires}>
          <Class style={headerIconStyle} />
        </Tooltip>
      );
    case T:
      return (
        <Tooltip title={tasks}>
          <AssignmentTurnedIn style={headerIconStyle} />
        </Tooltip>
      );
    case N:
      return (
        <Tooltip title={notes}>
          <ChatBubble style={headerIconStyle} />
        </Tooltip>
      );
    case H:
      return (
        <Tooltip title={hcpcs}>
          <div id="hcpcs">H</div>
        </Tooltip>
      );
    case F2F:
      return (
        <Tooltip title={faceToFace}>
          <Group style={headerIconStyle} />
        </Tooltip>
      );
    case arriveDistance:
      return (
        <Tooltip title={arriveDistance}>
          <FlightLand style={headerIconStyle} />
        </Tooltip>
      );
    case departDistance:
      return (
        <Tooltip title={departDistance}>
          <FlightTakeoff style={headerIconStyle} />
        </Tooltip>
      );

    default:
      break;
  }

  return <div />;
};
export const statusCellRenderer = (rowNode: any) => {
  const { status } = rowNode.data;
  return (
    <span>
      <Tooltip title={getVisitStatusLabel(status)}>
        <StatusDotDiv status={status}></StatusDotDiv>
      </Tooltip>
    </span>
  );
};
// EA created (EAC) shows green, offered only (EAO) shows red
export const electionAddendumStatusCellRenderer = (rowNode: any) => {
  const eaStatus = getElectionAddendumVisitStatus(
    rowNode.data?.visitattrsumdto,
  );
  if (!eaStatus) {
    return null;
  }
  return (
    <span style={{ color: eaStatus === 'created' ? 'green' : 'red' }}>EA</span>
  );
};

// the item in SFVAtt is going to be in acceptedAtts as they both take into account id -26
export const hopeStatusCellRenderer = (rowNode: any) => {
  const { visitattrsumdto } = rowNode.data;
  const [acceptedAtts, refusedAtts] = getHopeAttributes(visitattrsumdto);

  const hasAcceptedAtts = acceptedAtts?.length > 0;

  return (
    <>
      {hasAcceptedAtts && (
        <span style={{ color: 'green', paddingRight: '3px' }}>
          {acceptedAtts
            .map((att: any) => getAcceptedHopeStatus(att.shortname))
            .join(', ')}
        </span>
      )}
      {refusedAtts?.length > 0 && (
        <span style={{ color: 'red', paddingRight: '3px' }}>
          {refusedAtts
            .map((att: any) => getRefusedHopeStatus(att.shortname))
            .join(', ')}
        </span>
      )}
    </>
  );
};
